<template>
    <!-- OAuth callback 獨立頁面（不經過主 app 初始化） -->
    <router-view v-if="isAuthCallbackRoute" />

    <!-- 載入中 -->
    <div v-else-if="authLoading" class="loading-overlay">
        <div class="loading-content">
            <Spin size="large" />
            <p>載入中...</p>
        </div>
    </div>

    <!-- 未登入時顯示登入彈窗 -->
    <QuickAuth v-else-if="!gs.isLogin" />

    <!-- 已登入時顯示主應用 -->
    <template v-else>
        <!-- 登出確認對話框 -->
        <QuickLogout v-if="showLogout" @logout="handleLogoutComplete" @cancel="handleLogoutCancel" />
        <!-- 通知面板 -->
        <PageNotifications :notifications="currentNotifications" />
        <div id="layout" :content="pageName" :class="{ [pageName]: true, 'horizontal-nav': effectiveNavHorizontal }">
            <SideNav :is-login="gs.isLogin"
                :is-mobile="ui.isMobile"
                :selected-org-id="fieldScope?.selectedOrgId"
                :page-permissions="userPagePermissions"
                @toggle-drawer="toggleNavDrawer"
                @toggle-nav-orientation="handleNavOrientationChange"
                @toggle-nav-theme="handleNavThemeChange"
                @open-system-settings="openSystemSettings"></SideNav>
            <Layout id="content">
                <!-- 登入後使用 MultiViewManager -->
                <MultiViewManager :view="fieldScope" :is-router-alive="isRouterAlive" :is-dark-theme="isNavDarkTheme" ref="multiViewManager" />

                <!-- 桌布（當地圖關閉時顯示） -->
                <Wallpaper v-if="currentMapType === 'image' || currentMapType === 'solid'" :is-dark-mode="isNavDarkTheme" :map-type="currentMapType" />

                <!-- Windy 地圖 -->
                <WindyMap
                    v-if="['wind', 'temp', 'pressure'].includes(currentMapType)"
                    :data-field="fieldScope.dataField"
                    :map-type="currentMapType"
                />

                <!-- Google Maps -->
                <MapGoogle
                    v-else-if="currentMapType !== 'image' && currentMapType !== 'solid'"
                    :fields="fieldScope.fields"
                    :data-field="fieldScope.dataField"
                    ref="map"
                />
            </Layout>
        </div>

        <!-- 導航側邊欄 Drawer (使用全局 z-index) -->
        <Drawer
            width="280"
            :closable="false"
            v-model="showNavDrawer"
            placement="left"
            :styles="{ zIndex: navDrawerZIndex }"
        >
            <SideNavDrawer :organizations="fieldScope.organizations" v-model:selectedOrgId="fieldScope.selectedOrgId"
                :user-avatar-url="userAvatarUrl" :theme="theme" :version="version" :buildTime="buildTime"
                :available-modes="availableModes"
                @menu-select="mainMenuSelect" />
        </Drawer>

        <ImagePreview v-model="ui.showPreview" :preview-list="ui.previewSrcList"></ImagePreview>

        <Modal v-model="ui.showModal" draggable sticky scrollable :title="ui.modalTitle" width="80%" :footer="null">
            <component :is="ui.modalName" v-if="ui.modalName" :modalTitle="ui.modalTitle"
                :modalParameter="ui.modalParameter" />
        </Modal>

        <!-- 多層級業務 Drawer 容器 -->
        <template v-for="slot in ui.drawerSlots" :key="slot.id">
            <Drawer
                :title="slot.title"
                :width="ui.drawerWidth"
                :closable="false"
                v-model="slot.show"
                placement="right"
                @on-close="ui.closeDrawerById(slot.id)"
                :class-name="slot.fullBody ? 'drawer-fullbody' : ''"
                :styles="{ zIndex: slot.zIndex }"
            >
                <component
                    :is="getDrawerComponentForSlot(slot)"
                    v-if="slot.show && slot.name"
                    :drawerTitle="slot.title"
                    :drawerParameter="slot.parameter"
                    :drawerName="slot.name"
                    :openDrawer="ui.openDrawerStacked"
                    :closeDrawer="() => ui.closeDrawerById(slot.id)"
                    :drawerId="slot.id"
                />
            </Drawer>
        </template>

        <!-- 系統設定 Drawer (獨立管理，使用全局 z-index) -->
        <Drawer
            title="系統設定"
            class-name="drawer-fullbody"
            :closable="true"
            v-model="showSystemSettings"
            placement="right"
            :width="ui.drawerWidth"
            :styles="{ zIndex: systemSettingsZIndex }"
        >
            <SystemSettings />
        </Drawer>

        <!-- 案場過濾抽屜 -->
        <FieldFilterDrawer
            ref="fieldFilterDrawer"
            v-model:modelType="fieldScope.selectFilterKey"
            v-model:modelCity="fieldScope.selectCity"
            @change="handleFieldFilterChange"
        />

        <!-- 共用案場選擇抽屜 -->
        <FieldSelectorDrawer
            ref="fieldSelectorDrawer"
            v-model="sharedSelectedField"
            @change="handleSharedFieldSelect"
        />

        <!-- 案場過濾按鈕（浮動在右下角） -->
        <div class="nav-filter-button" @click="openFieldFilter" v-if="gs.isLogin">
            <i class="icofont-duotone icofont-filter"></i>
            <small>篩選</small>
        </div>

        <!-- 事件抽屜組件已移至 om Remote 模組 -->

        <!-- Service Worker 更新提示 -->
        <UpdatePrompt />

        <!-- 事件緩存同步指示器 -->
        <EventCacheSync ref="eventCacheSync" />

    </template>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted, provide, defineAsyncComponent, markRaw, getCurrentInstance, nextTick, type Component, type ComponentPublicInstance } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { auth } from '@shared/config/firebaseConfig'
import { relationshipService } from '@shared/services/organization/relationshipService'
import { organizationService } from '@shared/services/organization/organizationService'
import { fieldService } from '@shared/services/field/fieldService'
import eventService, { eventServiceInitializer } from '@shared/services/event'
import { authService } from '@shared/services/auth/authService'
import { configService } from '@shared/services/config/configService'
import { pagePermissionService, type UserPagePermissions } from '@shared/services/auth/pagePermissionService'

import PageNotifications from '@shared/components/common/PageNotifications.vue'
import MapGoogle from '@shared/components/map/MapGoogle.vue'
import WindyMap from '@shared/components/map/WindyMap.vue'
import SideNav from '@shared/components/navigation/SideNav.vue'
import SearchHeader from '@shared/components/navigation/SearchHeader.vue'
import SideNavDrawer from '@shared/components/navigation/SideNavDrawer.vue'
import MultiViewManager from '@shared/components/common/MultiViewManager.vue'
import Wallpaper from '@shared/components/common/Wallpaper.vue'
import { eventBus } from '@shared/stores/globalState'
import SystemSettings from '@shared/components/common/SystemSettings.vue'
import QuickAuth from '@shared/components/auth/QuickAuth.vue'
import QuickLogout from '@shared/components/auth/QuickLogout.vue'
import UpdatePrompt from '@shared/components/common/UpdatePrompt.vue'
import FieldFilterDrawer from '@shared/components/field/FieldFilterDrawer.vue'
import FieldSelectorDrawer from '@shared/components/field/FieldSelectorDrawer.vue'
import EventCacheSync from '@shared/components/common/EventCacheSync.vue'

import type { Field } from '@shared/types'
import type { RouteLocationNormalized } from 'vue-router'

// ============================================================================
// Type Definitions
// ============================================================================

/** 通知項目 */
interface NotificationItem {
    id?: string
    title?: string
    content?: string
    type?: string
    timestamp?: Date
    [key: string]: unknown
}

/** 事件通知資料 */
interface EventNotificationData {
    event: {
        field_name?: string
        field_id?: string
        title: string
        content?: string
        [key: string]: unknown
    }
    strategy: {
        showBrowserNotification?: boolean
        playSound?: boolean
        [key: string]: unknown
    }
}

/** 未讀計數 */
interface UnreadCounts {
    total: number
    [key: string]: number
}

/** 菜單項目 */
interface MenuItem {
    dir: string
    page: string
    title?: string
    action?: string
    [key: string]: unknown
}

/** 過濾條件變更事件 */
interface FilterChangeEvent {
    type: string
    city: string
}

/** 案場選擇選項 */
interface FieldSelectorOptions {
    callback?: ((field: Field | null) => void) | null
    selectedField?: Field | null
}

/** 拖曳偏移 */
interface DragOffset {
    x: number
    y: number
}

/** 模態位置 */
interface ModalPosition {
    top: number
    left: number
}

/** 菜單樣式 */
interface MenuStyle {
    right: string
    height: string
}

// Component ref types
type MultiViewManagerInstance = ComponentPublicInstance & {
    addView: (routeName: string, params?: Record<string, unknown>, title?: string, height?: number, width?: number) => unknown
}

type MapGoogleInstance = ComponentPublicInstance & {
    flyToField: (field: Field) => void
}

type FieldFilterDrawerInstance = ComponentPublicInstance & {
    openDrawer: () => void
    closeDrawer: () => void
    showDrawer?: boolean
}

type FieldSelectorDrawerInstance = ComponentPublicInstance & {
    openDrawer: () => void
    closeDrawer: () => void
    showDrawer: boolean
    searchKeyword: string
}

type EventCacheSyncInstance = ComponentPublicInstance

// ============================================================================
// Vue Instance & Router
// ============================================================================

const instance = getCurrentInstance()
const route = useRoute()
const router = useRouter()

// OAuth callback 路由不需要主 app 初始化，直接渲染 AuthCallback.vue
const isAuthCallbackRoute = computed(() => route.path === '/auth/callback')

// Global properties from Vue instance
const fieldScope = instance?.proxy?.fieldScope
const view = instance?.proxy?.view
const gs = instance?.proxy?.gs
const ui = instance?.proxy?.ui
const $Message = instance?.proxy?.$Message
const $swal = instance?.proxy?.$swal
const $forceUpdate = () => instance?.proxy?.$forceUpdate()

// ============================================================================
// Template Refs
// ============================================================================

const multiViewManager = ref<MultiViewManagerInstance | null>(null)
const map = ref<MapGoogleInstance | null>(null)
const fieldFilterDrawer = ref<FieldFilterDrawerInstance | null>(null)
const fieldSelectorDrawer = ref<FieldSelectorDrawerInstance | null>(null)
const eventCacheSync = ref<EventCacheSyncInstance | null>(null)

// ============================================================================
// Reactive State
// ============================================================================

const authLoading = ref(true)
const showNavDrawer = ref(false)
const showSystemSettings = ref(false)
// z-index 管理
const systemSettingsZIndex = ref(0)
const navDrawerZIndex = ref(0)
const sharedSelectedField = ref<Field | null>(null)
const fieldSelectionCallback = ref<((field: Field | null) => void) | null>(null)
const isFieldSelectionMode = ref(false)
const showLogout = ref(false)
const isRouterAlive = ref(true)
const initPath = ref('')
const originalTitle = ref(document.title)
const currentNotifications = ref<NotificationItem[]>([])
const isCollapsed = ref(false)
const showApps = ref(false)
const isNavHorizontal = ref(localStorage.getItem('navOrientation') !== 'vertical')
const isNavDarkTheme = ref(false)
const currentMapType = ref('satellite')
const showSelector = ref(false)
const selectFieldsCount = ref(0)
const tempTargetInfo = ref('')
const appsHover = ref<string | null>(null)
const menuRightSideWidth = ref('30px')
const menuStyle = ref<MenuStyle>({
    right: '30px',
    height: 'auto'
})
const disableSelectorBox = ref(false)
const disableFieldSelector = ref(false)
const theme = ref('light')
const modalPosition = ref<ModalPosition>({
    top: window.innerHeight * 0.1,
    left: window.innerWidth * 0.2,
})
const isDragging = ref(false)
const dragOffset = ref<DragOffset>({
    x: 0,
    y: 0
})
const version = ref(__APP_VERSION__)
const buildTime = ref(new Date(__BUILD_TIME__).toLocaleString('zh-TW'))
const isLocalhost = ref(window.location.hostname === 'localhost')
const isManuallyClosing = ref(false)
const userPermissions = ref<UserPagePermissions>({
    direct: [],
    inherited: [],
    all: [],
    modes: []
})

// ============================================================================
// Computed Properties
// ============================================================================

const effectiveNavHorizontal = computed(() => {
    return ui?.isMobile || isNavHorizontal.value
})

const pageName = computed(() => {
    return route.name as string || fieldScope?.pageName || ''
})

const searchKeyword = computed(() => {
    return fieldScope?.targetInfo || ''
})

const userAvatarUrl = computed(() => {
    return authService.currentUser?.photoURL || 'path/to/default/avatar.png'
})

// 可用的導航模式（從頁面權限推導）
const availableModes = computed(() => userPermissions.value.modes)

// 頁面權限列表
const userPagePermissions = computed(() => userPermissions.value.all)

// 本地抽屜組件映射
const localDrawerComponents: Record<string, () => Promise<any>> = {
    'drawer_UserUIEditor': () => import('@/drawer/UserUIEditor.vue'),
}

// om Remote 抽屜組件映射
const omDrawerComponents: Record<string, () => Promise<any>> = {
    'drawer_EquipmentDetail': () => import('om/drawer_EquipmentDetail'),
    'drawer_Chart': () => import('om/drawer_Chart'),
    'drawer_EventDetail': () => import('om/drawer_EventDetail'),
    'drawer_RiskEventDetail': () => import('om/drawer_RiskEventDetail'),
    'drawer_FieldDetail': () => import('om/drawer_FieldDetail'),
    'drawer_FieldEventCreate': () => import('om/drawer_FieldEventCreate'),
    'drawer_MemberDetail': () => import('om/drawer_MemberDetail'),
    'drawer_SystemDataEdit': () => import('om/drawer_SystemDataEdit'),
    'drawer_SystemEquipmentMapping': () => import('om/drawer_SystemEquipmentMapping'),
    'drawer_BillCreate': () => import('om/drawer_BillCreate'),
    'drawer_WorkOrderEdit': () => import('om/drawer_WorkOrderEdit'),
    'drawer_DocumentEdit': () => import('om/drawer_DocumentEdit'),
    'drawer_ExportDrawer': () => import('om/drawer_ExportDrawer'),
    'drawer_GeneratorDetail': () => import('om/drawer_GeneratorDetail'),
    'drawer_InverterDetail': () => import('om/drawer_InverterDetail'),
    'drawer_WarrantyDetail': () => import('om/drawer_WarrantyDetail'),
    'drawer_CreateMaintainRecord': () => import('om/drawer_CreateMaintainRecord'),
    'drawer_AIPromptHistory': () => import('om/drawer_AIPromptHistory'),
    'drawer_RepairOrderAssistant': () => import('om/drawer_RepairOrderAssistant'),
    'drawer_CollectedInfo': () => import('om/drawer_CollectedInfo'),
    'drawer_caseMaintenance': () => import('om/drawer_caseMaintenance'),
    'drawer_UserUIPreview': () => import('om/drawer_UserUIPreview'),
}

/**
 * 根據 slot 取得對應的 Drawer 組件
 */
function getDrawerComponentForSlot(slot: { name: string | Component; show: boolean }): Component | null {
    const drawerName = slot.name

    // 情況 1: 沒有 drawerName 或為 'undefined'
    if (!drawerName || drawerName === 'undefined') {
        return null
    }

    // 情況 2: 已經是組件物件
    if (typeof drawerName === 'object') {
        return markRaw(drawerName as Component)
    }

    // 情況 3: 本地抽屜組件（在 core 中定義）
    if (typeof drawerName === 'string' && localDrawerComponents[drawerName]) {
        return defineAsyncComponent(localDrawerComponents[drawerName])
    }

    // 情況 4: om Remote 抽屜組件
    if (typeof drawerName === 'string' && omDrawerComponents[drawerName]) {
        return defineAsyncComponent(omDrawerComponents[drawerName])
    }

    // 情況 5: 其他未知的 drawer
    if (typeof drawerName === 'string' && drawerName.startsWith('drawer_')) {
        console.warn('[Core Host] 找不到 Drawer 組件:', drawerName)
        return null
    }

    // 其他情況
    console.warn('[App] 無效的 drawerName:', drawerName)
    return null
}

// 向後相容：保留 drawerComponent computed（雖然不再使用，但可能有外部引用）
const drawerComponent = computed<Component | null>(() => {
    // 找到第一個開啟的 slot
    const activeSlot = ui?.drawerSlots?.find((s: { show: boolean }) => s.show)
    if (activeSlot) {
        return getDrawerComponentForSlot(activeSlot)
    }
    return null
})

// ============================================================================
// Provide
// ============================================================================

provide('reload', reload)

// ============================================================================
// Methods
// ============================================================================

// 初始化 EventService
async function initEventService(): Promise<void> {
    try {
        // 確保在正確的時機初始化 EventService
        await eventServiceInitializer.init()

        // 監聯 EventService 的事件
        setupEventServiceListeners()
    } catch (error) {
        console.error('[App] EventService 初始化失敗:', error)
    }
}

// 設置 EventService 事件監聽
function setupEventServiceListeners(): void {
    eventBus.on('eventService:notificationShow', handleEventServiceNotification)
    eventBus.on('eventService:unreadUpdated', handleUnreadCountUpdate)
    eventBus.on('eventService:statusChanged', handleEventServiceStatusChange)
}

// 處理 EventService 通知
function handleEventServiceNotification(data: EventNotificationData): void {
    const { event, strategy } = data

    // 根據策略處理不同類型的通知
    if (strategy.showBrowserNotification) {
        showBrowserNotification(event)
    }

    if (strategy.playSound) {
        playNotificationSound()
    }

    // 更新 PageNotifications 的資料
    updatePageNotifications()
}

// 處理未讀計數更新
function handleUnreadCountUpdate(counts: UnreadCounts): void {
    // 可以在這裡更新頁面標題或其他UI元素
    if (counts.total > 0) {
        document.title = `(${counts.total}) ${originalTitle.value || '系統名稱'}`
    } else {
        document.title = originalTitle.value || '系統名稱'
    }
}

// 處理 EventService 狀態變化
function handleEventServiceStatusChange(status: string): void {
    if (status === 'disconnected') {
        $Message?.warning('事件服務連接中斷')
    } else if (status === 'error') {
        $Message?.error('事件服務發生錯誤')
    }
}

// 顯示瀏覽器通知
function showBrowserNotification(event: EventNotificationData['event']): void {
    if ('Notification' in window && Notification.permission === 'granted') {
        const notification = new Notification(`${event.field_name} - ${event.title}`, {
            body: event.content || '',
            icon: '/favicon.ico',
            requireInteraction: false
        })

        notification.onclick = () => {
            window.focus()
            // 可以導航到相關頁面
            if (event.field_id) {
                router.push({
                    name: 'manage_EventsList',
                    query: { fieldId: event.field_id }
                })
            }
            notification.close()
        }

        // 5秒後自動關閉
        setTimeout(() => notification.close(), 5000)
    }
}

// 播放通知音效
function playNotificationSound(): void {
    try {
        // 創建音效（可以準備一個音效檔案）
        const audio = new Audio('/sounds/notification.mp3')
        audio.volume = 0.5
        audio.play().catch(() => {
            // 播放失敗，忽略
        })
    } catch (error) {
        // 無法播放通知音效，忽略
    }
}

// 更新 PageNotifications 資料
function updatePageNotifications(): void {
    // 通過 eventBus 通知 PageNotifications 組件更新
    eventBus.emit('pageNotifications:refresh')
}

function handleNotificationClick(tabTitle: string, item: unknown): void {
    // 通知點擊處理
}

function logoClick(): void {
    showNavDrawer.value = !showNavDrawer.value
}

function mainMenuSelect(event: Event, k: MenuItem | null): void {
    if (k) {
        showNavDrawer.value = false

        // 處理特殊動作（如登出）
        if (k.action === 'logout') {
            showLogoutDialog()
            return
        }

        // 將 dir 和 page 轉換為 routeName
        // 例如: dir='field', page='FieldDetail' => routeName='field_FieldDetail'
        const routeName = `${k.dir}_${k.page}`

        // 準備路由參數
        const routeParams: Record<string, unknown> = {
            params: {},
            query: {}
        }

        // 如果有當前案場，傳遞 fieldId
        if (fieldScope?.selectedFieldId) {
            routeParams.fieldId = fieldScope.selectedFieldId
        }

        // 如果有當前組織，傳遞 orgId
        if (fieldScope?.orgId) {
            routeParams.orgId = fieldScope.orgId
        }

        // 使用 addView() 創建新視圖
        multiViewManager.value?.addView(
            routeName,
            routeParams,
            k.title || k.page
        )
    }
}

function handleFieldSelected(field: Field): void {
    // 為該案場創建獨立的 view
    if (multiViewManager.value) {
        const viewTitle = field.name || `案場 ${field.id}`

        // 創建新的 view 用於顯示案場詳細資訊
        multiViewManager.value.addView(
            'field_FieldDetail',
            { orgId: fieldScope?.selectedOrgId, fieldId: field.id },
            viewTitle,
            420 // 指定高度為 420px（與地圖打開的案場視圖相同）
        )
    } else {
        console.error('[App] MultiViewManager 不存在！')
    }
}

async function afterPageChange(data: { pageName?: string }): Promise<void> {
    disableFieldSelector.value = data.pageName?.startsWith('patrol') || false
    disableSelectorBox.value = data.pageName?.startsWith('manage_Organization') || false
    disableFieldSelector.value = disableFieldSelector.value || (data.pageName?.startsWith('manage_Organization') || false)
    fieldScope?.updateUrlSearch?.()
}

async function afterChangeField(): Promise<void> {
    try {
        if (!fieldScope?.selectedFieldId) {
            console.warn('沒有選擇案場 ID')
            return
        }

        const currentField = fieldScope?.currentField
        if (!currentField) {
            console.warn('找不到對應的案場')
            return
        }

        try {
            const fieldRoles = await relationshipService.getRoles('field', fieldScope.selectedFieldId)
            if (fieldScope?.relationships) {
                fieldScope.relationships['fields'] = fieldRoles
            }
        } catch (error) {
            console.error('獲取案場角色時發生錯誤:', error)
        }

        // 如果案場有位置信息，則飛行到位置
        map.value?.flyToField(currentField)

        fieldScope?.updateUrlSearch?.()
    } catch (error) {
        console.error('切換案場時發生錯誤:', error)
    }
}

async function afterChangeOrganization(): Promise<void> {
    try {
        let fields: Field[] = []
        try {
            const orgRoles = await relationshipService.getRoles('organization', fieldScope?.selectedOrgId)
            if (fieldScope?.relationships) {
                fieldScope.relationships['organizations'] = orgRoles
            }
            fields = (orgRoles.data?.fields || [])
                .filter((field: Field | null | undefined) => field !== null && field !== undefined)
            eventBus.emit('field:focus-changed', fieldScope?.currentOrg?.focus_fields || [])
        } catch (e) {
            console.error('獲取選定組織的角色時發生錯誤:', e)
            fields = []
        }

        // 更新案場資料
        const fieldData = await fieldService.getFieldsByOrganization(fieldScope?.selectedOrgId)

        fieldData.forEach((f: Field & { city?: string }) => {
            const cityName = f.address?.substr(0, 3) || ''
            f.city = fieldScope?.normalizeCity ? fieldScope.normalizeCity(cityName) : cityName
        })
        // 更新 view.fields
        if (fieldScope) {
            fieldScope.fields = fieldData
            // 同時更新 dataField，確保地圖顯示正確
            fieldScope.dataField = [...fieldData]
            // 更新城市列表
            fieldScope.updateCityList?.()
        }

        // 載入用戶頁面權限
        await loadUserPermissions()
    } catch (error) {
        console.error('載入案場資料時發生錯誤:', error)
        $Message?.error('載入案場資料時發生錯誤')
    }
}

async function afterFocusFieldChanged(newFocusFields: string[]): Promise<void> {
    if (fieldScope) {
        fieldScope.focusFields = newFocusFields
    }
}

async function afterLogin(): Promise<void> {
    try {
        // 登入完成後啟動事件服務（統一入口）
        await eventServiceInitializer.start()

        // 檢查是否有待處理的邀請碼
        const pendingInviteCode = localStorage.getItem('pendingInviteCode')
        if (pendingInviteCode) {
            console.log('[App] 發現待處理的邀請碼:', pendingInviteCode)
            // 清除 localStorage 中的邀請碼
            localStorage.removeItem('pendingInviteCode')
            // 延遲開啟加入組織頁面，確保 UI 已準備好
            setTimeout(() => {
                ui?.openView?.('manage', 'OrganizationJoin', '加入組織', { invite: pendingInviteCode })
            }, 500)
        }
    } catch (error) {
        console.error('[App] afterLogin 發生錯誤:', error)
    }
}

function updateRelationships(
    user?: unknown,
    orgs?: unknown,
    fields?: unknown
): void {
    if (fieldScope?.relationships) {
        if (user) {
            fieldScope.relationships['users'] = user
        }
        if (orgs) {
            fieldScope.relationships['organizations'] = orgs
        }
        if (fields) {
            fieldScope.relationships['fields'] = fields
        }
    }
}

async function loadFields(orgs: Array<{ id: string }>): Promise<Field[][]> {
    const dataFields = await Promise.all(orgs.map(async (org) => {
        return await fieldService.getField(org.id)
    }))
    return dataFields
}

async function afterUserInit(user: { uid?: string; id?: string; lastSelectedOrgId?: string | null } | null): Promise<void> {
    if (gs?.isLogin) {
        // 使用傳入的 user 參數，而非 authService.currentUser（可能尚未設置）
        const userId = user?.uid || user?.id || authService.currentUser?.uid
        if (!userId) {
            console.error('[App] afterUserInit: 無法取得 userId')
            return
        }

        // 清除緩存確保獲取最新數據
        relationshipService.clearCache()

        // 直接從 user 對象中取得 lastSelectedOrgId（已在 authService.buildUserFromSession 查詢過）
        const lastSelectedOrgId = user?.lastSelectedOrgId ||
                                  authService.currentUser?.lastSelectedOrgId ||
                                  null

        // 查詢用戶角色
        const userRoles = await relationshipService.getRoles('user', userId)

        // 【並行化】第二批：根據第一批結果繼續
        const userOrgsIds = userRoles.data.organizations.map((org: { id: string }) => org.id)
        if (fieldScope) {
            fieldScope.organizations = await organizationService.getOrganizationByIds(userOrgsIds)

            // 緩存組織列表到 localStorage（避免下次載入抽屜組織選擇空白）
            localStorage.setItem('cached_organizations', JSON.stringify(fieldScope.organizations))

            updateRelationships({ user: userRoles, orgs: fieldScope.organizations })

            // 優先使用 URL 參數，否則使用已設置的 selectedOrgId 或 lastSelectedOrgId
            let selectedOrgId = fieldScope.getParam?.('orgId') || lastSelectedOrgId || ''

            // 驗證選擇的組織是否仍然存在於使用者的組織清單中
            if (selectedOrgId) {
                const orgExists = fieldScope.organizations.some((org: { id: string }) => org.id === selectedOrgId)
                if (!orgExists) {
                    console.warn('[App] 選擇的組織不存在於使用者權限中，重置選擇:', selectedOrgId)
                    selectedOrgId = ''
                }
            }

            fieldScope.selectedOrgId = selectedOrgId
            fieldScope.selectedFieldId = fieldScope.getParam?.('fieldId') || ''
            localStorage.setItem('currentUserId', userId)

            // 從 Supabase 讀取過濾器偏好設定
            try {
                const filterPrefs = await authService.getFilterPreferences(userId)
                if (filterPrefs) {
                    fieldScope.selectFilter = filterPrefs.filter
                    fieldScope.selectCity = filterPrefs.city
                    // 同步更新 selectFilterKey
                    const filterType = fieldScope.filterTypeList?.find((ft: { title: string; key: string }) => ft.title === filterPrefs.filter)
                    fieldScope.selectFilterKey = filterType?.key || 'MONITOR'
                    // 同步到 localStorage
                    localStorage.setItem('view_selectFilter', filterPrefs.filter)
                    localStorage.setItem('view_selectCity', filterPrefs.city)
                }
            } catch (error) {
                console.warn('[App] 讀取過濾器偏好設定失敗:', error)
            }

            if (!selectedOrgId || selectedOrgId === 'null') {
                const firstOrg = fieldScope.organizations[0]
                // 當未選擇組織時，自動選擇第一個組織
                if (firstOrg) {
                    fieldScope.changeOrg?.(firstOrg)
                }
            }
        }

        eventBus.emit('org:changed')

        // 如果需強制更新視圖，可以使用 nextTick
        await nextTick()
        $forceUpdate?.()
    } else {
        if (location.pathname === '' || location.pathname === '/') {
            router.push('/')
        }
    }
}

function reload(): void {
    isRouterAlive.value = false
    nextTick(() => {
        isRouterAlive.value = true
    })
}

/**
 * 載入用戶頁面權限
 */
async function loadUserPermissions(): Promise<void> {
    const userId = authService.currentUser?.uid
    const orgId = fieldScope?.selectedOrgId

    if (!userId || !orgId) {
        console.log('[App] 無法載入權限：缺少 userId 或 orgId')
        return
    }

    try {
        userPermissions.value = await pagePermissionService.getUserPagePermissionsByFunction(
            userId,
            orgId
        )
        console.log('[App] 已載入用戶權限:', {
            modes: userPermissions.value.modes,
            permCount: userPermissions.value.all.length
        })
    } catch (error) {
        console.error('[App] 載入用戶權限失敗:', error)
    }
}

function toggleNavDrawer(): void {
    showNavDrawer.value = !showNavDrawer.value
}

function closeNavDrawer(): void {
    showNavDrawer.value = false
}

function openSystemSettings(): void {
    // 關閉其他競爭抽屜
    if (fieldFilterDrawer.value) {
        fieldFilterDrawer.value.closeDrawer()
    }
    showSystemSettings.value = true
}

function openFieldFilter(): void {
    // 關閉設定抽屜
    showSystemSettings.value = false

    // 如果在案場選擇模式，切換抽屜
    if (isFieldSelectionMode.value) {
        const selectorOpen = fieldSelectorDrawer.value?.showDrawer
        const filterOpen = fieldFilterDrawer.value?.showDrawer

        if (selectorOpen) {
            // 案場選擇器開著 → 關閉它，打開篩選器
            if (fieldSelectorDrawer.value) {
                fieldSelectorDrawer.value.showDrawer = false
            }
            if (fieldFilterDrawer.value) {
                fieldFilterDrawer.value.openDrawer()
            }
        } else if (filterOpen) {
            // 篩選器開著 → 關閉它，打開案場選擇器
            fieldFilterDrawer.value?.closeDrawer()
            if (fieldSelectorDrawer.value) {
                fieldSelectorDrawer.value.searchKeyword = ''
                fieldSelectorDrawer.value.showDrawer = true
            }
        } else {
            // 都沒開 → 打開案場選擇器
            if (fieldSelectorDrawer.value) {
                fieldSelectorDrawer.value.searchKeyword = ''
                fieldSelectorDrawer.value.showDrawer = true
            }
        }
    } else {
        // 非案場選擇模式，正常打開篩選器
        if (fieldSelectorDrawer.value) {
            fieldSelectorDrawer.value.showDrawer = false
        }
        if (fieldFilterDrawer.value) {
            fieldFilterDrawer.value.openDrawer()
        }
    }
}

// 全局快捷鍵處理
function handleGlobalKeydown(event: KeyboardEvent): void {
    // F2: 開啟篩選 drawer
    if (event.key === 'F2') {
        event.preventDefault()
        if (gs.value?.isLogin) {
            openFieldFilter()
        }
    }
}

function openFieldSelector(options: FieldSelectorOptions = {}): void {
    // 進入案場選擇模式
    isFieldSelectionMode.value = true
    // 關閉其他競爭抽屜
    showSystemSettings.value = false
    if (fieldFilterDrawer.value) {
        fieldFilterDrawer.value.closeDrawer()
    }
    // 保存回調（如果有）
    fieldSelectionCallback.value = options.callback || null
    // 設定已選案場（如果有）
    if (options.selectedField) {
        sharedSelectedField.value = options.selectedField
    }
    // 直接設置 showDrawer（避免調用 openDrawer 導致無限遞迴）
    if (fieldSelectorDrawer.value) {
        fieldSelectorDrawer.value.searchKeyword = ''
        fieldSelectorDrawer.value.showDrawer = true
    }
}

function handleSharedFieldSelect(field: Field | null): void {
    // 選擇完成，退出案場選擇模式
    isFieldSelectionMode.value = false
    // 如果有回調，執行它
    if (fieldSelectionCallback.value) {
        fieldSelectionCallback.value(field)
    }
    // 也發出事件，讓其他頁面可以監聽
    eventBus.emit('field-selection-result', field)
}

function closeCompetingDrawers(): void {
    // 關閉所有競爭抽屜（設定、案場過濾、案場選擇）
    showSystemSettings.value = false
    if (fieldFilterDrawer.value) {
        fieldFilterDrawer.value.closeDrawer()
    }
    if (fieldSelectorDrawer.value) {
        fieldSelectorDrawer.value.closeDrawer()
    }
}

function handleFieldFilterChange({ type, city }: FilterChangeEvent): void {
    // 過濾條件改變時，更新 view 中的狀態
    // 將 key 轉換為 title（與 FieldSelector 使用相同的變數）
    const filterTypeList = view?.filterTypeList || []
    const filterType = filterTypeList.find((f: { key: string; title: string }) => f.key === type)
    const filterTitle = filterType?.title || '全部類型'
    const cityValue = city === '' ? '全部地區' : city

    // 更新 view 狀態（使用與 FieldSelector 相同的變數名稱）
    if (fieldScope) {
        fieldScope.selectFilter = filterTitle
        fieldScope.selectFilterKey = type
        fieldScope.selectCity = cityValue
    }

    // 保存到 localStorage（與 FieldSelector 相同）
    localStorage.setItem('view_selectFilter', filterTitle)
    localStorage.setItem('view_selectCity', cityValue)

    // 如果 FieldSelector 已掛載，直接同步其內部狀態
    // （這樣不需要修改 FieldSelector.vue 即可實現即時同步）
    if (fieldScope?.fieldSelector) {
        fieldScope.fieldSelector.selectFilter = filterTitle
        fieldScope.fieldSelector.selectCity = cityValue
        // 觸發 FieldSelector 重新過濾
        fieldScope.fieldSelector.applyFilters?.()
    }

    // 重新應用過濾
    fieldScope?.updateCityList?.()

    // 通知其他元件過濾條件已改變
    eventBus.emit('field-filter-changed', { type, city })
}

function handleNavOrientationChange(isHorizontal: boolean): void {
    isNavHorizontal.value = isHorizontal
    // 通知其他組件導航方向已改變
    eventBus.emit('system:nav-orientation-changed', isHorizontal)
}

function handleNavThemeChange(isDark: boolean): void {
    isNavDarkTheme.value = isDark
    // 更新 body class 以套用全域深色主題
    if (isDark) {
        document.body.classList.add('dark-theme')
    } else {
        document.body.classList.remove('dark-theme')
    }
    // 通知其他組件主題已改變
    eventBus.emit('ui:theme-changed', isDark)
}

function handleMapTypeChange(mapType: string): void {
    // 只保存 type 字串
    currentMapType.value = mapType
    localStorage.setItem('mapType', mapType)
}

function handleMapTypeChanged(mapType: string | unknown): void {
    // 確保只保存字串
    currentMapType.value = typeof mapType === 'string' ? mapType : String(mapType || 'satellite')
}

function afterFieldDataUpdated(fieldId: string): void {
    $Message?.success(`案場資料已更新`)
    fieldService.getField(fieldId).then((field: Field) => {
        if (fieldScope) {
            fieldScope.fields = fieldScope.fields.map((f: Field) => f.id === fieldId ? field : f)
        }
    })
}

function showLogoutDialog(): void {
    showLogout.value = true
}

// 處理 eventBus 開啟抽屜事件（供 Remote 模組使用）
// 使用 openDrawerStacked 而非 openDrawer，避免再次觸發 eventBus 造成無限迴圈
function handleOpenDrawer(payload: { name: string; title?: string; params?: Record<string, unknown>; fullBody?: boolean }): void {
    ui?.closeAllDrawers?.()
    ui?.openDrawerStacked?.(payload.name, payload.title || '', payload.params || {}, payload.fullBody || false)
}

// 處理 eventBus 關閉抽屜事件
function handleCloseDrawer(): void {
    ui?.closeDrawer?.()
}

function handleLogoutComplete(): void {
    showLogout.value = false
    // QuickAuth 會自動顯示，因為 view.isLogin 會變成 false
}

function handleLogoutCancel(): void {
    showLogout.value = false
}

function addNewNotification(notification: NotificationItem): void {
    // 添加新通知到列表頂部
    currentNotifications.value.unshift({
        ...notification,
        timestamp: new Date()
    })

    // 限制通知數量，保持最新的 20 條
    if (currentNotifications.value.length > 20) {
        currentNotifications.value = currentNotifications.value.slice(0, 20)
    }
}

// 處理路由變更
function handleRouteChange(to: RouteLocationNormalized): void {
    fieldScope?.setParam?.('page', to.name as string)

    // 檢查並更新選擇器狀態
    updateSelectorState(to.name as string)
}

// 更新選擇器狀態
function updateSelectorState(pageName: string | null | undefined): void {
    if (fieldScope) {
        fieldScope.disableFieldSelector = pageName?.startsWith('patrol') || false
        fieldScope.disableSelectorBox = pageName?.startsWith('manage_Organization') || false
        fieldScope.disableFieldSelector = fieldScope.disableFieldSelector ||
            (pageName?.startsWith('manage_Organization') || false)
    }
}

// 檢查 Supabase 登入狀態（使用 authService）
async function checkSupabaseAuth(): Promise<unknown> {
    try {
        const user = await authService.handleOAuthCallback()

        if (user) {
            // 設置登入狀態
            if (view?.globalState) {
                if (gs) {
                    gs.isLogin = true
                }
                fieldScope.globalState.currentUser = user
            }
            eventBus.emit('user:login', user)
        }

        return user
    } catch (error: unknown) {
        // 處理 OAuth 錯誤
        const err = error as { code?: string; message?: string }
        if (err.code === 'access_denied') {
            $Message?.warning('使用者取消了登入授權')
        } else if (err.code !== 'server_error') {
            $Message?.error('登入失敗: ' + (err.message || '未知錯誤'))
        }
        authService.cleanupUrlParams()
        return null
    }
}

// 集中處理 Auth 狀態檢查
// OAuth 回調已由 /auth/callback 頁面（AuthCallback.vue）處理
// 這裡只需要檢查現有 session 狀態
async function handleAuthRedirect(): Promise<unknown> {
    try {
        // 檢查 Supabase 登入狀態
        const supabaseUser = await checkSupabaseAuth()
        if (supabaseUser) {
            return supabaseUser
        }

        // 檢查當前登入狀態
        const currentUser = auth.currentUser
        if (currentUser) {
            eventBus.emit('user:login', currentUser)
            return currentUser
        }

        // 檢查 localStorage 中的最近登入用戶
        const lastLoginUserStr = localStorage.getItem('auth_last_login_user')
        if (lastLoginUserStr) {
            try {
                const lastLoginUser = JSON.parse(lastLoginUserStr) as { timestamp?: number }
                const timestamp = lastLoginUser.timestamp || 0

                if (Date.now() - timestamp < 5 * 60 * 1000) {
                    $Message?.warning('登入狀態已過期，請重新登入')
                }
            } catch (e) {
                console.error('[App] 解析最近登入記錄失敗:', e)
            }
        }
    } catch (error) {
        console.error('[App] ❌ 處理認證檢查時發生錯誤:', error)
        $Message?.error('登入處理失敗，請重試')
    }

    return null
}

function addView(routeName: string, params: Record<string, unknown> = {}, title: string | null = null): unknown {
    if (multiViewManager.value) {
        return multiViewManager.value.addView(routeName, params, title || routeName)
    }
    return null
}

// ============================================================================
// Watch
// ============================================================================

watch(route, (to) => {
    handleRouteChange(to)
})

// 監聽系統設定 Drawer 開關，管理 z-index
watch(showSystemSettings, (newVal) => {
    if (newVal) {
        systemSettingsZIndex.value = ui?.getNextZIndex?.('system', 'settings') || 1000
    } else {
        ui?.releaseZIndex?.('system', 'settings')
        systemSettingsZIndex.value = 0
    }
})

// 監聽導航側邊欄 Drawer 開關，管理 z-index
watch(showNavDrawer, (newVal) => {
    if (newVal) {
        navDrawerZIndex.value = ui?.getNextZIndex?.('nav', 'main') || 1000
    } else {
        ui?.releaseZIndex?.('nav', 'main')
        navDrawerZIndex.value = 0
    }
})

// ============================================================================
// Lifecycle Hooks
// ============================================================================

onMounted(async () => {
    // 檢查 URL 是否有邀請碼參數，如果有則存入 localStorage
    const urlParams = new URLSearchParams(window.location.search)
    const inviteCode = urlParams.get('invite_code')
    if (inviteCode) {
        localStorage.setItem('pendingInviteCode', inviteCode)
        // 清除 URL 參數，保持乾淨
        const cleanUrl = window.location.pathname + window.location.hash
        window.history.replaceState({}, document.title, cleanUrl || '/')
        console.log('[App] 邀請碼已保存:', inviteCode)
    }

    // 讀取導航欄主題狀態
    const savedTheme = localStorage.getItem('navTheme')
    if (savedTheme === 'dark') {
        isNavDarkTheme.value = true
        document.body.classList.add('dark-theme')
    }

    // 監聽導航欄相關事件
    eventBus.on('system:nav-orientation-change', handleNavOrientationChange)
    eventBus.on('ui:theme-change', handleNavThemeChange)
    eventBus.on('ui:map-type-change', handleMapTypeChange)
    eventBus.on('ui:map-type-changed', handleMapTypeChanged)
    eventBus.on('field:selected', handleFieldSelected)
    eventBus.on('ui:open-settings', openSystemSettings)
    eventBus.on('ui:open-field-filter', openFieldFilter)
    eventBus.on('ui:open-field-selector', openFieldSelector)
    eventBus.on('ui:open-drawer', handleOpenDrawer)
    eventBus.on('ui:close-drawer', handleCloseDrawer)
    eventBus.on('ui:close-nav-drawer', closeNavDrawer)

    // 全局快捷鍵監聽
    window.addEventListener('keydown', handleGlobalKeydown)

    // 讀取保存的地圖類型
    const savedMapType = localStorage.getItem('mapType')
    if (savedMapType) {
        currentMapType.value = savedMapType
    }

    // 最優先：設置 Promise 用於追踪重定向處理結果
    const redirectPromise = handleAuthRedirect()

    initPath.value = location.pathname
    eventBus.on('user:init', afterUserInit)
    eventBus.on('user:login', afterLogin)
    eventBus.on('user:logout', fieldScope?.afterLogout)
    eventBus.on('org:changed', afterChangeOrganization)
    eventBus.on('field:changed', afterChangeField)
    eventBus.on('page:changed', afterPageChange)
    eventBus.on('field:data-updated', afterFieldDataUpdated)
    eventBus.on('field:focus-changed', afterFocusFieldChanged)
    eventBus.on('ui:show-logout', showLogoutDialog)
    ui?.handleWindowResize?.()
    window.addEventListener('resize', ui?.handleWindowResize)

    // 等待重定向處理完成
    await redirectPromise

    // 認證檢查完成，關閉載入狀態
    authLoading.value = false

    // 初始化 EventService
    initEventService()

    // 注意：認證狀態監聽已由 authService 統一管理
    // globalState 透過 authService.onAuthStateChange() 訂閱認證事件
    // 此處不再需要重複的 supabase.auth.onAuthStateChange 監聽器

    const swal = $swal
    ;(window as Window & { alert: (msg: string, timeout?: number, allowOutsideClick?: boolean) => void }).alert = function (msg: string, timeout?: number, allowOutsideClick?: boolean) {
        if (typeof allowOutsideClick === 'undefined') allowOutsideClick = true
        swal?.close()
        setTimeout(function () {
            if (timeout !== undefined) {
                swal?.fire({
                    title: '',
                    html: msg,
                    timer: timeout,
                    showConfirmButton: false,
                    allowOutsideClick: allowOutsideClick,
                })
            } else {
                if (msg) {
                    swal?.fire({
                        title: msg,
                    })
                }
            }
        }, 100)
    }

    // 添加全局導航守衛
    router.beforeEach((to, from, next) => {
        next()
    })
})

onUnmounted(() => {
    eventBus.off('user:init', afterUserInit)
    eventBus.off('user:login', afterLogin)
    eventBus.off('user:logout', fieldScope?.afterLogout)
    eventBus.off('org:changed', afterChangeOrganization)
    eventBus.off('field:changed', afterChangeField)
    eventBus.off('field:data-updated', afterFieldDataUpdated)
    eventBus.off('field:focus-changed', afterFocusFieldChanged)
    eventBus.off('ui:show-logout', showLogoutDialog)

    // 清理導航欄相關監聯器
    eventBus.off('system:nav-orientation-change', handleNavOrientationChange)
    eventBus.off('ui:theme-change', handleNavThemeChange)
    eventBus.off('ui:map-type-change', handleMapTypeChange)
    eventBus.off('ui:map-type-changed', handleMapTypeChanged)
    eventBus.off('field:selected', handleFieldSelected)
    eventBus.off('ui:open-settings', openSystemSettings)
    eventBus.off('ui:open-field-filter', openFieldFilter)
    eventBus.off('ui:open-field-selector', openFieldSelector)
    eventBus.off('ui:open-drawer', handleOpenDrawer)
    eventBus.off('ui:close-drawer', handleCloseDrawer)
    eventBus.off('ui:close-nav-drawer', closeNavDrawer)

    // 清理 EventService 監聽器
    eventBus.off('eventService:notificationShow', handleEventServiceNotification)
    eventBus.off('eventService:unreadUpdated', handleUnreadCountUpdate)
    eventBus.off('eventService:statusChanged', handleEventServiceStatusChange)

    window.removeEventListener('resize', ui?.handleWindowResize)
    window.removeEventListener('keydown', handleGlobalKeydown)

    // 停止 EventService
    if (eventServiceInitializer) {
        eventServiceInitializer.stop()
    }
})

// ============================================================================
// Expose Methods for Parent Components
// ============================================================================

defineExpose({
    reload,
    addView
})
</script>

<style scoped lang="stylus">
.mobileMenu
    transition: right 0.5s ease, height 0.3s ease;
    position: fixed;
    background: #fff;
    border: solid 1px #9999;
    z-index: 9999999;
    right: 30px;
    top: 53px;
    border-radius: 4px;
    width: 130px;
    overflow: hidden;

    .ivu-list-item
        height: 42px;

.selectorBox
    display: inline-block;
    padding: 4px 8px 8px;
    border-left: solid 1px rgba(153, 153, 153, 0.6);
    border-right: solid 1px rgba(153, 153, 153, 0.6);
    margin: -3px 5px;
    cursor: pointer;
    font-size: 14px;
    color: #333;
    width: 25%;
    max-width 225px;
    height: 40px;
    overflow: hidden;
    float: left;
    position: relative;
    text-align left;

    &:hover
        box-shadow: 0 0 0 2px rgba(45, 140, 240, .2);
        background: #f0f0f0;

    &.disable
        color: #dddddd;

    span
        background: #ddd;
        border-radius: 4px;
        padding: 3px;
        position absolute;
        font-size 11px;
        top: 4px;
        right: 8px;
        height 22px;
        margin-top 3px;
        line-height 16px;

#menu
    small
        float: left;
        font-size: 10px;
        font-weight: 800;
        margin: 0;
        padding: 0 10px;

        &.disable
            color: #dddddd;

    .ivu-select
        width: 25%;
        max-width 225px;
        margin-top: -3px;
        float: left;
        height: 36px;

    ::v-deep(.ivu-select-selection)
        height: 36px;
        line-height: 36px;
        border-radius 0;
        font-size 13px;
        color #333;

        &:hover
            box-shadow: 0 0 0 2px rgba(45, 140, 240, .2);
            border: solid 1px #9999;
            background: #f0f0f0;

    ::v-deep(.disable .ivu-select-selection)
        color: #dddddd;
        border: solid 1px #9999;
        cursor: not-allowed;

    ::v-deep(.ivu-select-selected-value)
        height: 34px;
        line-height: 34px;

    ::v-deep(.ivu-select-placeholder)
        height: 34px;
        line-height: 34px;

@media (max-width: 768px)
    #menu
        small
            display: none

        .selectorBox
        .ivu-select
            width: 30%;

#windy
    height: calc(100% + 20px);
    width: calc(100% - 70px);
    position: fixed;
    top: 0;
    bottom: 0;
    left: 80px;
    right: 0;
    z-index: 1;

    // 可以根據需要為不同的縮放級別添加特定的樣式
    &.zoom-level-8
        // 縮放級別 8 的特定樣式
    &.zoom-level-9
        // 縮放級別 9 的特定樣式
    // ... 其他放級別的樣式
.divIcon
    position: relative;


#layout
    position: relative

#content
    transition: width 0.3s ease

.ivu-modal-wrap
  display: flex;
  align-items: center;
  justify-content: center;

.ivu-modal
  top: 0 !important;
  display: flex;
  flex-direction: column;

.ivu-modal-content
  flex: 1;
  display: flex;
  flex-direction: column;
  max-height: 90vh;

.ivu-modal-body
  flex: 1;
  overflow-y: auto;
  padding: 0;

.windy-control-panel
    position: absolute;
    top: 65px;
    border-radius: 5px;
    padding: 5px;
    left: 95px;
    z-index: 80;

    .windy-btn-list
        .btn
            border-radius: 15px;
            font-size: 12px;
            padding: 5px 10px;
            margin-right: 8px;
            cursor: pointer;
            background: #fff;
            border: solid 1px #ddd;

            &.active
                background-color: #666;
                color: #fff;

.ivu-modal-header
    background: #f8f8f8;
    p
        font-size: 16px;

.ivu-modal-close
    font-size: 12px;
    position: absolute;
    right: -12px;
    top: -12px;

    background: #fff;
    border-radius: 50%;

    .ivu-icon-ios-close
        color: #666;
        font-weight: 800;


.loading-overlay
    position: fixed
    inset: 0
    background: #f5f7fa
    display: flex
    align-items: center
    justify-content: center
    z-index: 9999

    .loading-content
        text-align: center

        p
            margin-top: 16px
            color: #666
            font-size: 14px

// 過濾按鈕 - 浮動在右邊，hover 時滑出
.nav-filter-button
    position fixed
    bottom 16px
    right 0
    z-index 962
    display flex
    flex-direction column
    align-items center
    justify-content center
    gap 2px
    width 36px
    height 36px
    padding 6px 6px 3px 6px
    cursor pointer
    transition all 0.3s cubic-bezier(0.4, 0, 0.2, 1)
    user-select none
    box-sizing border-box
    background rgba(255, 255, 255, 0.9)
    border-radius 6px 0 0 6px
    box-shadow 0 2px 8px rgba(0, 0, 0, 0.1)
    transform translateX(8px)

    i
        font-size 18px
        flex-shrink 0
        width 18px
        height 18px
        display flex
        align-items center
        justify-content center
        color #888
        line-height 1
        transition all 0.3s cubic-bezier(0.4, 0, 0.2, 1)

    small
        font-size 0
        color #888
        text-align center
        opacity 0
        transition all 0.3s cubic-bezier(0.4, 0, 0.2, 1)
        overflow hidden
        white-space nowrap

    &:hover
        width 60px
        height 60px
        padding 8px 5px
        gap 4px
        transform translateX(0)
        background #fff
        border-radius 12px 0 0 12px
        box-shadow 0 4px 16px rgba(90, 159, 255, 0.25)

        i
            font-size 24px
            width 24px
            height 24px
            color #5A9FFF

        small
            font-size 12px
            opacity 1
            color #5A9FFF

// 深色主題下的過濾按鈕
.dark-theme .nav-filter-button
    background rgba(26, 58, 92, 0.9)
    box-shadow 0 2px 8px rgba(0, 0, 0, 0.2)

    i, small
        color #aaa

    &:hover
        background #1a3a5c
        box-shadow 0 4px 16px rgba(90, 159, 255, 0.3)

        i, small
            color #5A9FFF

</style>
